// Resolves the active UnitSystem for a reporting view.
//
// ReportingSport ('soccer' | 'american_football') is a different string shape
// from SportTypeValues ('Soccer' | 'AmericanFootball'). This module bridges
// the two so views can pass `sportConfig.sport` directly without knowing
// about the core sports type system.
import type { ReportingSport } from '@frontend/core'
import { getSport } from '@frontend/core/lib/domains/sports'
import type {
  Sport,
  SportTypeValues
} from '@frontend/core/lib/domains/sports/data_types'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import { useUnitsSystem } from '@frontend/react'
import { useMemo } from 'react'

const reportingToSportType: Record<ReportingSport, SportTypeValues> = {
  soccer: 'Soccer',
  american_football: 'AmericanFootball',
  canadian_football: 'CanadianFootball'
}

// Non-hook bridge for call sites that need a real `Sport` object outside of
// component render (e.g. building a modal's ChildComponent), mirroring how
// Match Tracker resolves `getSport(session.data.sport)` at its own download
// modal's call site.
export function getReportingSport(sport: ReportingSport): Sport {
  return getSport(reportingToSportType[sport])
}

export function useReportingUnitSystem(sport: ReportingSport): UnitSystem {
  const sportObj = useMemo(() => getReportingSport(sport), [sport])
  return useUnitsSystem(sportObj)
}
